import { Checkbox, Stack, Text, Tile, Title } from "@assetwatch/ui";
import { useState } from "react";
import { ProcedureItemTypeIcon } from "../snowflakes/procedures/ProcedureItemTypeIcon";
import type {
  ProcedureAuthoringDraft,
  ProcedureItem,
} from "./procedureAuthoring";

function PreviewField({ item }: { item: ProcedureItem }) {
  if (item.type === "text")
    return (
      <input
        aria-label={`${item.label} response`}
        className="w-full max-w-[420px] rounded-(--radius-sm) border border-input-default-outline bg-input-default-background p-(--spacing-8)"
        placeholder="Free text response"
        disabled
      />
    );
  if (item.type === "number")
    return (
      <div className="flex items-center gap-(--spacing-8)">
        <input
          aria-label={`${item.label} response`}
          className="w-full max-w-[140px] rounded-(--radius-sm) border border-input-default-outline bg-input-default-background p-(--spacing-8)"
          placeholder="Enter value"
          type="number"
          disabled
        />
        {item.unit ? <Text tone="subtle">{item.unit}</Text> : null}
      </div>
    );
  if (item.type === "photo")
    return <div className="text-text-subtle">Attach photo or file</div>;
  if (item.type === "checkbox") return <Checkbox label="Confirmed" disabled />;
  return (
    <Stack gap={8}>
      {item.options.map((option) => (
        <label
          className="flex items-center gap-(--spacing-8) text-text-default"
          key={option}
        >
          <input type="radio" name={item.id} disabled />
          {option}
        </label>
      ))}
    </Stack>
  );
}

function PreviewItem({ item }: { item: ProcedureItem }) {
  return (
    <div className="flex items-start gap-(--spacing-8)">
      <ProcedureItemTypeIcon type={item.type} size="sm" />
      <Stack gap={8}>
        <div>
          <Text>
            {item.label || "Untitled item"}
            {item.required ? " *" : ""}
          </Text>
          {item.description ? (
            <Text tone="subtle">{item.description}</Text>
          ) : null}
        </div>
        <PreviewField item={item} />
      </Stack>
    </div>
  );
}

export function ProcedurePreview({
  draft,
}: {
  draft: ProcedureAuthoringDraft;
}) {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const itemCount = [
    ...draft.looseItems,
    ...draft.looseSections.flatMap((section) => section.items),
    ...draft.groups.flatMap((group) =>
      group.sections.flatMap((section) => section.items),
    ),
  ].length;
  return (
    <div className="w-full max-w-[720px]">
      <Tile padding="none" width="full">
        <div className="border-b border-outline-subtle bg-surface-container-low px-(--spacing-16) py-(--spacing-12)">
          <GroupHeader
            title={draft.title || "Untitled procedure"}
            itemCount={itemCount}
          />
        </div>
        <div className="p-(--spacing-16)">
          <Stack gap={24}>
            {draft.description ? (
              <Text tone="subtle">{draft.description}</Text>
            ) : null}
            {draft.looseSections.map((section) => (
              <Stack gap={16} key={section.id}>
                <div className="uppercase tracking-wide">
                  <Text tone="subtle">
                    {section.title || "Untitled section"}
                  </Text>
                </div>
                {section.items.map((item) => (
                  <PreviewItem item={item} key={item.id} />
                ))}
              </Stack>
            ))}
            {draft.groups.map((group) => (
              <Stack gap={20} key={group.id}>
                <button
                  aria-expanded={!collapsed[group.id]}
                  className="flex items-center gap-(--spacing-8) border-b border-outline-subtle pb-(--spacing-10) text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-button-focus-ring"
                  onClick={() =>
                    setCollapsed((current) => ({
                      ...current,
                      [group.id]: !current[group.id],
                    }))
                  }
                  type="button"
                >
                  <span aria-hidden="true" className="text-text-subtle">
                    {collapsed[group.id] ? "›" : "⌄"}
                  </span>
                  <Text weight="semibold">
                    {group.title || "Untitled heading"}
                  </Text>
                </button>
                {!collapsed[group.id]
                  ? group.sections.map((section) => (
                      <Stack gap={16} key={section.id}>
                        <button
                          aria-expanded={!collapsed[section.id]}
                          className="flex items-center gap-(--spacing-6) text-left text-text-subtle focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-button-focus-ring"
                          onClick={() =>
                            setCollapsed((current) => ({
                              ...current,
                              [section.id]: !current[section.id],
                            }))
                          }
                          type="button"
                        >
                          <span aria-hidden="true">
                            {collapsed[section.id] ? "›" : "⌄"}
                          </span>
                          {section.title || "Untitled section"}
                        </button>
                        {!collapsed[section.id]
                          ? section.items.map((item) => (
                              <PreviewItem item={item} key={item.id} />
                            ))
                          : null}
                      </Stack>
                    ))
                  : null}
              </Stack>
            ))}
            {draft.looseItems.length ? (
              <Stack gap={16}>
                <Text tone="subtle">Unsectioned items</Text>
                {draft.looseItems.map((item) => (
                  <PreviewItem item={item} key={item.id} />
                ))}
              </Stack>
            ) : null}
          </Stack>
        </div>
      </Tile>
    </div>
  );
}

function GroupHeader({
  title,
  itemCount,
}: {
  title: string;
  itemCount: number;
}) {
  return (
    <div className="flex items-center justify-between gap-(--spacing-12) border-b border-outline-subtle pb-(--spacing-12)">
      <Title as="h2" size="lg">
        {title}
      </Title>
      <Text tone="subtle">{itemCount} fields · read-only preview</Text>
    </div>
  );
}
